import { useState, useRef, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { processChatWithMcp, getMcpHealthStatus } from './mcpApi';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  loading?: boolean;
}

interface ParaLexTraceChatProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ParaLexTraceChat({ isOpen, onClose }: ParaLexTraceChatProps) {
  const { keycloak } = useKeycloak();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'system',        content: '🤖 Welcome to ParaLex MCP Agent! I can help you analyze your EdgeLex system with multi-AI orchestration, job traces, failures, and provide DevOps insights. What would you like to know?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [serviceHealth, setServiceHealth] = useState<'healthy' | 'unhealthy' | 'unknown'>('unknown');
  const [queryStartTime, setQueryStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentAbortController, setCurrentAbortController] = useState<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check service health on mount
  useEffect(() => {
    if (isOpen) {
      checkServiceHealth();
    }
  }, [isOpen]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  // Timer for query duration
  useEffect(() => {
    if (queryStartTime && isLoading) {
      // Start timer
      timerIntervalRef.current = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - queryStartTime.getTime()) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
    } else {
      // Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [queryStartTime, isLoading]);

  const checkServiceHealth = async () => {
    if (!keycloak?.token) return;

    try {
      const health = await getMcpHealthStatus(keycloak.token);
      setServiceHealth(health.status === 'healthy' ? 'healthy' : 'unhealthy');
    } catch (error) {
      console.error('[ParaLexTraceChat] Health check failed:', error);
      setServiceHealth('unhealthy');
    }
  };

  const cancelCurrentQuery = () => {
    if (currentAbortController) {
      currentAbortController.abort();
      setCurrentAbortController(null);
      setIsLoading(false);
      setQueryStartTime(null);
      setElapsedSeconds(0);

      // Update the loading message to show cancellation
      setMessages(prev => {
        const updated = [...prev];
        const lastMessage = updated[updated.length - 1];
        if (lastMessage && lastMessage.loading) {
          updated[updated.length - 1] = {
            ...lastMessage,
            content: '❌ Query cancelled by user',
            loading: false
          };
        }
        return updated;
      });
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !keycloak?.token) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    const loadingMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: '🤔 Analyzing your EdgeLex system...',
      timestamp: new Date(),
      loading: true
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    // Start timer
    const startTime = new Date();
    setQueryStartTime(startTime);
    setElapsedSeconds(0);

    // Create abort controller for 130-second timeout (slightly longer than backend)
    const abortController = new AbortController();
    setCurrentAbortController(abortController);
    const timeoutId = setTimeout(() => abortController.abort(), 130000); // 130 seconds

    try {
      // Build a chat request for MCP-enhanced ParaLex conversational interface
      const chatRequest = {
        message: inputMessage.trim(),
        realmId: 'system',
        userId: keycloak.subject || 'unknown',
        sessionId: `system-chat-${Date.now()}`,
        mcpStrategy: 'auto' as const,
        includeSystemContext: true
      };

      const chatResult = await processChatWithMcp(chatRequest, keycloak.token);
      
      clearTimeout(timeoutId);
      
      // Format the MCP chat response
      let responseContent = chatResult.response || 'I received your message but couldn\'t provide a specific response.';
      
      // Add MCP metadata information
      if (chatResult.aiProvidersUsed && chatResult.aiProvidersUsed.length > 0) {
        responseContent += `\n\n🤖 **AI Providers:** ${chatResult.aiProvidersUsed.join(', ')}`;
      }
      
      if (chatResult.cost && chatResult.cost > 0) {
        responseContent += `\n💰 **Cost:** $${chatResult.cost.toFixed(4)}`;
      }
      
      if (chatResult.executionTime) {
        responseContent += `\n⏱️ **Response Time:** ${chatResult.executionTime}ms`;
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 2).toString(),
        type: 'assistant',
        content: responseContent,
        timestamp: new Date()
      };

      setMessages(prev => prev.slice(0, -1).concat([assistantMessage]));

    } catch (error) {
      clearTimeout(timeoutId);
      
      let errorContent = '❌ Sorry, I encountered an error: ';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorContent += 'Request timed out after 130 seconds. MCP ParaLex may be processing a complex analysis. Please try a simpler question or wait a moment.';
        } else {
          errorContent += error.message;
        }
      } else {
        errorContent += 'Unknown error occurred';
      }
      
      errorContent += '. Please try again or check if the MCP ParaLex service is running.';

      const errorMessage: ChatMessage = {
        id: (Date.now() + 2).toString(),
        type: 'assistant',
        content: errorContent,
        timestamp: new Date()
      };

      setMessages(prev => prev.slice(0, -1).concat([errorMessage]));
    } finally {
      setIsLoading(false);
      setCurrentAbortController(null);
      setQueryStartTime(null);
      setElapsedSeconds(0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: '1',
        type: 'system',
        content: '🤖 Chat cleared! How can I help you with your EdgeLex system using MCP multi-AI analysis?',
        timestamp: new Date()
      }
    ]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-850 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🤖</span>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                ParaLex MCP Chat
              </h2>
              <div className={`w-2 h-2 rounded-full ${
                serviceHealth === 'healthy' ? 'bg-green-500 animate-pulse' :
                serviceHealth === 'unhealthy' ? 'bg-red-500' : 'bg-gray-400'
              }`} title={`Service is ${serviceHealth}`} />
            </div>
            <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 rounded-full text-xs font-medium">
              MCP Multi-AI
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={clearChat}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              🗑️ Clear
            </button>
            <button
              onClick={checkServiceHealth}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              ⟳ Refresh
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 dark:hover:text-white p-2"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Service Health Warning */}
        {serviceHealth === 'unhealthy' && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-red-600 dark:text-red-400">⚠️</span>
              <span className="text-red-800 dark:text-red-200">
                MCP ParaLex service is unavailable. Check that MCP servers and AI providers are running.
              </span>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.type === 'user'
                    ? 'bg-purple-600 text-white'
                    : message.type === 'system'
                    ? 'bg-lex-accent-soft text-lex-accent'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                }`}
              >
                <div className="whitespace-pre-wrap text-sm">
                  {message.loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      {message.content}
                    </div>
                  ) : (
                    message.content
                  )}
                </div>
                <div className={`text-xs mt-2 ${
                  message.type === 'user' ? 'text-purple-200' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {/* Query Timer and Cancel Button */}
          {isLoading && queryStartTime && (
            <div className="flex items-center justify-between mb-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-2 text-sm text-purple-700 dark:text-purple-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <span>Query running for {Math.floor(elapsedSeconds / 60)}:{(elapsedSeconds % 60).toString().padStart(2, '0')}</span>
              </div>
              <button
                onClick={cancelCurrentQuery}
                className="px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg transition-colors border border-red-200 dark:border-red-800"
              >
                ❌ Cancel Query
              </button>
            </div>
          )}
          
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about your EdgeLex system using MCP multi-AI analysis - jobs, failures, DevOps insights..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                rows={1}
                style={{ maxHeight: '120px' }}
                disabled={isLoading || serviceHealth !== 'healthy'}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || serviceHealth !== 'healthy'}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                !inputMessage.trim() || isLoading || serviceHealth !== 'healthy'
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                '🚀'
              )}
            </button>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Press Enter to send, Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
  );
} 